import { leaveVideoMeeting } from "@core/features/videoMeeting";
import { leaveChimeSession, videoEventBus } from "@core/features/video";

type MeetingManager = Parameters<typeof leaveChimeSession>[0];

export type PerformVideoLeaveArgs = {
  roomId: number | null;
  // Accept both number and string — the store's meetingId source is
  // typed as number in some surfaces and string in others (the hook
  // narrows the slice to string, the dialog reads the slice's number
  // form via useVideoMeetingDetails). The videochat:left event bus
  // contract is string, so we coerce on emit.
  meetingId: number | string | null;
  chimeExternalUserId: string | null;
  meetingManager: MeetingManager;
};

// Module-scoped in-flight Promise singleton — concurrent calls share the
// same Promise so dedup spans every call site that goes through this
// function. Mirrors the pattern in useJoinVideoMeeting.clearExistingSession.
//
// Closes the adjacent-second double-leave class observed in production
// session 11 of meeting 5936 (one user emitted two `left reason=normal`
// events 1s apart from the dialog and the lifecycle hook independently).
// The same-second dedup from 68902f07 is preserved upstream but becomes
// redundant once both call sites flow through this function.
let inFlight: Promise<void> | null = null;

export function performVideoLeave(args: PerformVideoLeaveArgs): Promise<void> {
  if (inFlight) return inFlight;

  inFlight = (async () => {
    const { roomId, meetingId, chimeExternalUserId, meetingManager } = args;

    if (roomId === null && meetingId === null) return;

    if (roomId !== null) {
      try {
        await leaveVideoMeeting(roomId, chimeExternalUserId);
      } catch {
        // Non-fatal — backend reconciler will reap stale entries.
      }
    }

    if (meetingId !== null) {
      await leaveChimeSession(meetingManager);
      videoEventBus.emitEvent("videochat:left", { meetingId: String(meetingId) });
    }
  })();

  return inFlight.finally(() => {
    inFlight = null;
  });
}
